import { Dialog } from "@blofin/blofin-ui";
import { FC, useEffect, useState } from "react";
import { dispatchChatbotParamsSync } from "../../common/CustomEvent";
import { useStoreState } from "../store/useStore";
import { activeStyles, itemStyles } from "./styles";
import Cookies from "js-cookie";
import useLangUpdate from "../hooks/useLangUpdate";
import { headerName } from "@/nav-localized/config";
import CheckLineIcon from "@/nav-localized/assets/header/check-line.svg?react";
import clsx from "clsx";

interface LanguageDialogProps {
  open: boolean;
  cancel: () => void;
  customChangeLang?: (lan: string) => void;
}

const Language = {
  en: "English",
  // 'zh-tw': '繁體中文',
  // 'ko-kr': '한국어',
  "uk-ua": "Українська",
  vi: "Tiếng Việt",
  ru: "Русский",
  es: "Español (Argentina)",
  fr: "Français (Afrique)",
  tr: "Türkçe",
  pt: "Português (Internacional)",
  ar: "العربية"
};

type LanguageItemType = keyof typeof Language;

const DEFAULT_LOCALES = Object.keys(Language) as string[];

const LanguageContent: FC<{ customChangeLang?: (lan: string) => void }> = ({
  customChangeLang
}) => {
  const { isLogin, isDemoTrading, locale, theme } = useStoreState();

  const [locales, setLocales] = useState<string[]>([]);

  const { LangUpdate } = useLangUpdate();

  const getCurrentUrl = (lang: string) => {
    const router = typeof window !== "undefined" ? window.next?.router : undefined;

    if (!router) {
      const pathname = window.location.pathname;
      const search = window.location.search;
      const locList = DEFAULT_LOCALES;
      let cleanPath = pathname + search;
      for (const loc of locList) {
        if (loc === "default") continue;
        if (cleanPath === `/${loc}` || cleanPath.startsWith(`/${loc}/`)) {
          cleanPath = cleanPath.slice(`/${loc}`.length) || "/";
          break;
        }
      }
      const basePath = "";
      if (basePath === "" && cleanPath.split("?")[0] === "/") {
        return lang === "en" ? "/" : `/${lang}`;
      }
      return lang === "en" ? `${basePath}${cleanPath}` : `${basePath}/${lang}${cleanPath}`;
    }

    const { basePath, asPath, locales = [] } = router;

    let cleanPath = asPath;
    for (const loc of locales) {
      if (loc === "default") continue;
      if (cleanPath === `/${loc}` || cleanPath.startsWith(`/${loc}/`)) {
        cleanPath = cleanPath.slice(`/${loc}`.length) || "/";
        break;
      }
    }

    if (basePath === "" && cleanPath === "/") {
      return lang === "en" ? "/" : `/${lang}`;
    }

    return lang === "en" ? `${basePath}${cleanPath}` : `${basePath}/${lang}${cleanPath}`;
  };

  const changeLang = (item: string) => {
    Cookies.set("lang", item || "en", { expires: 999 });
    dispatchChatbotParamsSync({ locale: item || "en" });

    if (customChangeLang) {
      customChangeLang(item);
      return;
    }

    setTimeout(async () => {
      try {
        if (isLogin) {
          if (!isDemoTrading) {
            await LangUpdate({
              lang: item || "en"
            });
          }
          window.location.href = getCurrentUrl(item);
        } else {
          window.location.href = getCurrentUrl(item);
        }
      } catch (error) {
        console.error(error);
      }
    }, 0);
  };

  useEffect(() => {
    const router = window.next?.router;
    if (router?.locales?.length) {
      setLocales(router.locales);
    } else {
      setLocales(DEFAULT_LOCALES);
    }
  }, []);

  return (
    <>
      <ul className="z-1000 w-full text-[14px]">
        {locales.map((item, index) => {
          return (
            <a
              key={index}
              onClick={() => changeLang(item)}
              className={clsx(
                itemStyles({ theme }),
                locale === item ? activeStyles({ theme }) : "",
                "flex justify-between cursor-pointer"
              )}
            >
              {Language[item as LanguageItemType]}
              {locale === item && (
                <CheckLineIcon
                  className={clsx(
                    "ml-auto h-4 w-4 shrink-0",
                    theme === "dark" ? "text-dark-primary" : "text-light-primary"
                  )}
                />
              )}
            </a>
          );
        })}
      </ul>
    </>
  );
};

const LanguageDialog: FC<LanguageDialogProps> = ({ open, cancel, customChangeLang }) => {
  const { theme, showTheme } = useStoreState();

  return (
    <Dialog
      className={headerName}
      theme={showTheme ? theme : "light"}
      title={null}
      size="medium"
      content={<LanguageContent customChangeLang={customChangeLang} />}
      open={open}
      cancel={cancel}
      footer={null}
    ></Dialog>
  );
};

export default LanguageDialog;
export { LanguageContent };
